import { z } from "zod";
import {
    DEFAULT_ENABLED_TRANSIT_MODE_CATEGORIES,
    normalizeEnabledTransitModeCategories,
    TRANSIT_MODE_CATEGORY_IDS,
    type TransitModeCategoryId,
} from "../../../tools/transitPrefs.tools";
import { definePersistedSlice } from "../runtime/definePersistedSlice";

/** Supported public transit mode category filters for itinerary search UI. */
export type TransitModeFilter = TransitModeCategoryId;

/** Allowed maximum-transfer preference values (API and UI radio ids). */
export type MaxTransfersPreference = 0 | 1 | 2 | 3 | 20;

const TRANSIT_MODE_FILTERS = TRANSIT_MODE_CATEGORY_IDS as unknown as [
    TransitModeCategoryId,
    TransitModeCategoryId,
    ...TransitModeCategoryId[],
];

const DEFAULT_ENABLED_MODES: TransitModeFilter[] = [ ...DEFAULT_ENABLED_TRANSIT_MODE_CATEGORIES ];

type TransitPrefsInput = {
    "enabledModes": TransitModeFilter[];
    "maxTransfers": MaxTransfersPreference;
};

/**
 * Normalizes transit preferences after parse (legacy maxTransfers, non-empty modes).
 *
 * @param params Raw fields before normalization rules.
 * @returns Coherent transit preference object for routing and UI.
 */
const normalizeTransitPrefs = ( params: TransitPrefsInput ): TransitPrefsInput => {
    const normalizedModes = normalizeEnabledTransitModeCategories( params.enabledModes );
    const enabledModes = normalizedModes.length > 0 ? normalizedModes : DEFAULT_ENABLED_MODES;

    return {
        ...params,
        enabledModes,
    };
};

const MaxTransfersPreferenceSchema = z.preprocess(
    ( value ) => ( value === 4 ? 20 : value ),
    z.union( [
        z.literal( 0 ),
        z.literal( 1 ),
        z.literal( 2 ),
        z.literal( 3 ),
        z.literal( 20 ),
    ] ).catch( 20 ).
        default( 20 ),
);

const TransitModeFilterSchema = z.preprocess(
    ( value ) => {
        if ( !Array.isArray( value ) ) {
            return value;
        }

        return normalizeEnabledTransitModeCategories( value.filter( ( item ): item is string => typeof item === "string" ) );
    },
    z.array( z.enum( TRANSIT_MODE_FILTERS ) ).default( DEFAULT_ENABLED_MODES ),
);

/**
 * Zod schema for public transit filter preferences.
 *
 * @remarks The `.transform` maps legacy `maxTransfers: 4` to `20` and guards empty mode lists.
 */
const TransitPrefsSchema = z.object( {
    "enabledModes": TransitModeFilterSchema,
    "maxTransfers": MaxTransfersPreferenceSchema,
} ).transform( normalizeTransitPrefs );

/** In-memory and persisted shape for transit preferences. */
export type TransitPrefs = z.infer<typeof TransitPrefsSchema>;

/** Parsed defaults used before hydration and as decode fallback. */
export const TRANSIT_PREFS_DEFAULT: TransitPrefs = TransitPrefsSchema.parse( {} );

/**
 * Migrates v1 persisted enabled modes to v2 UI category ids.
 *
 * @param previous Raw slice data from version 1.
 * @returns Slice data with all transit categories enabled by default.
 */
const migrateTransitPrefsV1ToV2 = ( previous: unknown ): unknown => {
    if ( previous === null || typeof previous !== "object" ) {
        return previous;
    }

    return {
        ...previous,
        "enabledModes": [ ...DEFAULT_ENABLED_MODES ],
    };
};

/**
 * Ensures persisted v2 envelopes expose every transit category as enabled.
 *
 * @param previous Raw slice data from version 2.
 * @returns Slice data with all transit categories enabled.
 */
const migrateTransitPrefsV2ToV3 = ( previous: unknown ): unknown => {
    if ( previous === null || typeof previous !== "object" ) {
        return previous;
    }

    return {
        ...previous,
        "enabledModes": [ ...DEFAULT_ENABLED_MODES ],
    };
};

/** Persisted slice: transit mode filters and maximum transfers. */
export const transitPrefsSlice = definePersistedSlice<TransitPrefs>( {
    "key":            "transitPrefs",
    "currentVersion": 3,
    "storage":        "preferences",
    "eager":          true,
    "schema":         TransitPrefsSchema,
    "default":        () => TRANSIT_PREFS_DEFAULT,
    "migrations":     {
        "2": migrateTransitPrefsV1ToV2,
        "3": migrateTransitPrefsV2ToV3,
    },
} );
